import { TEST_IDS } from '@va/constants';
import { useIsWelcomePage } from '@va/dashboard/util-hooks';
import { IconProps, MouseScrollIcon, VideoCameraIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { contributorRole } from '@va/standalone/shared/selectors';
import { Button, fontWeights, Heading3, IconWithBackground, Paragraph, paragraphSizes } from '@va/ui/design-system';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { useSelector } from 'react-redux';

type FeatureOnboardingProps = {
  illustration: ReactNode;
  title: string;
  subtitle: string;
  content: ReactNode;
  buttonText: string;
  buttonTooltip?: string;
  onGetStarted: () => void;
  onClickThrough?: () => void;
  onFeatureVideo?: () => void;
  isInteractiveHelpEnabled?: boolean;
  hasPermission: boolean;
};

export function FeatureOnboarding({
  illustration,
  title,
  subtitle,
  content,
  buttonText,
  buttonTooltip,
  onGetStarted,
  onClickThrough,
  onFeatureVideo,
  isInteractiveHelpEnabled,
  hasPermission,
}: FeatureOnboardingProps) {
  const translate = useTranslate();

  useIsWelcomePage();

  return (
    <div className='max-w-480px mx-auto mt-12 flex flex-col items-center'>
      {illustration}
      <Heading3
        className='pt-12'
        colorClassName='text-gray-code'
        weight={fontWeights.semibold}
        data-testid={TEST_IDS.helpers.createFeatureOnboardingElementId('title')}
      >
        {title}
      </Heading3>
      <Paragraph
        size={paragraphSizes.large}
        className='pt-6px text-center'
        colorClassName='text-gray-charcoal'
        data-testid={TEST_IDS.helpers.createFeatureOnboardingElementId('subTitle')}
      >
        {subtitle}
      </Paragraph>
      {content}
      {isInteractiveHelpEnabled && (
        <div className='flex gap-3 w-full mb-12px'>
          <Button
            className='w-full'
            icon={() => <MouseScrollIcon color='#696969' />}
            text={translate('button.clickThroughTour')}
            color='tertiary'
            onClick={onClickThrough}
            data-testid={TEST_IDS.helpers.createFeatureOnboardingElementId('click-through')}
          />
          <Button
            className='w-full'
            icon={() => <VideoCameraIcon color='#696969' />}
            text={translate('button.featureVideo')}
            color='tertiary'
            onClick={onFeatureVideo}
            data-testid={TEST_IDS.helpers.createFeatureOnboardingElementId('video')}
          />
        </div>
      )}
      <Button
        disabled={!hasPermission}
        text={buttonText}
        tooltip={hasPermission ? buttonTooltip : translate('tooltips.contributorRole')}
        className='w-full'
        onClick={onGetStarted}
        data-testid={TEST_IDS.helpers.createFeatureOnboardingElementId('get-started')}
      />
    </div>
  );
}

export type FeatureHighlightsProps = {
  className?: string;
  featureList: {
    icon: ({ className, color }: IconProps) => JSX.Element;
    title: string;
    info: string;
    iconBackgroundColor?: string;
    infoColorClassName?: string;
  }[];
};

export function FeatureHighlights({ className, featureList }: FeatureHighlightsProps) {
  return (
    <div className={classNames('flex flex-col gap-6', className)}>
      {featureList.map(({ title, info, icon: Icon, iconBackgroundColor, infoColorClassName }, index) => {
        return (
          <div className='flex items-center gap-18px' key={index}>
            <IconWithBackground
              icon={() => <Icon color='white' />}
              className={`${iconBackgroundColor ?? 'bg-green-pure'} shrink-0}`}
            />
            <div className='flex flex-col'>
              <Paragraph
                size={paragraphSizes.large}
                weight={fontWeights.semibold}
                colorClassName='text-gray-charcoal'
                data-testid={TEST_IDS.helpers.createFeatureOnboardingElementId('highlight-title')}
              >
                {title}
              </Paragraph>
              <Paragraph
                weight={fontWeights.semibold}
                colorClassName={infoColorClassName ?? 'text-gray-devil'}
                data-testid={TEST_IDS.helpers.createFeatureOnboardingElementId('highlight-info')}
              >
                {info}
              </Paragraph>
            </div>
          </div>
        );
      })}
    </div>
  );
}
